import { useEffect, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { Label } from "@/components/ui/label";
import { CalendarDays, Clock, Check, CheckCircle, Zap, AlertTriangle } from "lucide-react";
import { format, addDays, isBefore, isAfter, startOfDay, isSameDay } from "date-fns";
import { TIME_SLOTS } from "./constants";

export default function SchedulingStep({ preferredDates, updatePreferredDate, rushBooking, setRushBooking }) {
  // Refs for scrolling to date options
  const dateOptionRefs = useRef([null, null, null]);
  // Track last scrolled index to avoid repeated scrolls
  const lastScrolledRef = useRef(-1);

  // Auto-scroll to next date option when BOTH date AND time are selected
  useEffect(() => {
    // Find completed options (have both date AND time_slot)
    for (let i = 0; i < preferredDates.length - 1; i++) {
      const isCurrentComplete = preferredDates[i].date && preferredDates[i].time_slot;
      const isNextIncomplete = !preferredDates[i + 1].date || !preferredDates[i + 1].time_slot;
      
      // If this option is complete and next is not, and we haven't scrolled there yet
      if (isCurrentComplete && isNextIncomplete && lastScrolledRef.current < i + 1) {
        lastScrolledRef.current = i + 1;
        const nextRef = dateOptionRefs.current[i + 1];
        if (nextRef) {
          setTimeout(() => {
            nextRef.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 300);
        }
        break;
      }
    }
  }, [preferredDates]);
  // Date restrictions: 5 days from today, no Sundays, max 30 days out
  const minDate = addDays(startOfDay(new Date()), 5);
  const maxDate = addDays(startOfDay(new Date()), 30);

  const isDateDisabled = (date) => {
    if (isBefore(date, minDate) || isAfter(date, maxDate)) return true;
    if (date.getDay() === 0) return true; // Block Sundays
    return false;
  };

  // Check if a date+time combination is already selected in another preference
  const isSlotTaken = (currentIndex, date, timeSlotId) => {
    if (!date || !timeSlotId) return false;
    
    return preferredDates.some((pref, idx) => {
      if (idx === currentIndex) return false; // Skip current preference
      if (!pref.date || !pref.time_slot) return false;
      return isSameDay(pref.date, date) && pref.time_slot === timeSlotId;
    });
  };

  // Get list of already selected date+time combinations (excluding current index)
  const getSelectedCombinations = (excludeIndex) => {
    return preferredDates
      .filter((pref, idx) => idx !== excludeIndex && pref.date && pref.time_slot)
      .map(pref => ({
        date: pref.date,
        time_slot: pref.time_slot,
        label: `${format(pref.date, 'MMM d')} - ${TIME_SLOTS.find(s => s.id === pref.time_slot)?.label}`
      }));
  };

  return (
    <div className="max-w-3xl mx-auto animate-fade-in-up" data-testid="step-3-schedule">
      <h2 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit' }}>
        When works best for you?
      </h2>
      <p className="text-slate-600 mb-4">
        Please select <strong>up to 3 preferred date and time options</strong> below.
      </p>
      
      {/* Important Notice */}
      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
        <div className="flex items-start gap-3">
          <CalendarDays className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-blue-900">How scheduling works:</p>
            <ul className="mt-2 text-sm text-blue-800 space-y-1">
              <li>Select your <strong>top 3 preferred dates and time windows</strong></li>
              <li>Each date/time combination can only be selected once</li>
              <li>Rolling Suds will review your preferences and our availability</li>
              <li>We'll contact you to <strong>confirm the final date and time</strong></li>
            </ul>
          </div>
        </div>
      </div>

      {/* Rush Booking Option */}
      <Card 
        className={`mb-6 border-2 cursor-pointer transition-all ${
          rushBooking 
            ? 'border-amber-500 bg-gradient-to-r from-amber-50 to-orange-50 shadow-lg' 
            : 'border-amber-200 hover:border-amber-300 bg-amber-50/50'
        }`}
        onClick={() => setRushBooking(!rushBooking)}
        data-testid="rush-booking-card"
      >
        <CardContent className="p-5">
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
              rushBooking ? 'bg-amber-500' : 'bg-amber-200'
            }`}>
              <Zap className={`w-6 h-6 ${rushBooking ? 'text-white' : 'text-amber-700'}`} />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <h3 className="font-bold text-lg text-slate-900">Need Service ASAP?</h3>
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                  rushBooking 
                    ? 'bg-amber-500 border-amber-500' 
                    : 'border-slate-300 bg-white'
                }`}>
                  {rushBooking && <Check className="w-4 h-4 text-white" />}
                </div>
              </div>
              <p className="text-slate-600 mt-1">
                Can't find a date that works? Select this option and we'll do our best to accommodate your schedule.
              </p>
              <div className="mt-3 flex items-center gap-2 text-amber-700 bg-amber-100 px-3 py-2 rounded-lg">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                <p className="text-sm font-medium">
                  Premium rates may apply if we can schedule your job when needed
                </p>
              </div>
            </div>
          </div>
          {rushBooking && (
            <div className="mt-4 pt-4 border-t border-amber-200">
              <p className="text-sm text-slate-600">
                <CheckCircle className="w-4 h-4 text-emerald-500 inline mr-2" />
                <strong>Rush request selected.</strong> Our team will contact you within 24 hours to discuss expedited scheduling options and any applicable premium rates.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Divider with "OR" */}
      {!rushBooking && (
        <div className="flex items-center gap-4 mb-6">
          <div className="flex-1 h-px bg-slate-200"></div>
          <span className="text-sm font-medium text-slate-400 uppercase">Or select preferred dates</span>
          <div className="flex-1 h-px bg-slate-200"></div>
        </div>
      )}

      {/* Date Selection Cards - dimmed when rush booking is selected */}
      <div className={`space-y-6 transition-all ${rushBooking ? 'opacity-40 pointer-events-none' : ''}`}>
        {rushBooking && (
          <div className="text-center py-4 text-slate-500 italic">
            Date selection is optional when "Need Service ASAP" is selected
          </div>
        )}
        {[0, 1, 2].map((index) => {
          const currentDate = preferredDates[index].date;
          const selectedCombos = getSelectedCombinations(index);
          
          return (
            <Card 
              key={index} 
              ref={(el) => (dateOptionRefs.current[index] = el)}
              id={`date-option-${index}`} 
              className={`border-2 ${
              preferredDates[index].date && preferredDates[index].time_slot
                ? 'border-blue-500 bg-blue-50/30'
                : index === 0 ? 'border-blue-300 bg-white' : 'border-slate-200'
            }`}>
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    preferredDates[index].date && preferredDates[index].time_slot
                      ? 'bg-blue-600 text-white'
                      : index === 0 ? 'bg-blue-500 text-white' : 'bg-slate-200 text-slate-600'
                  }`}>
                    {index + 1}
                  </div>
                  <h3 className="font-semibold text-slate-900">
                    {index === 0 ? 'First Preference' : index === 1 ? 'Second Preference' : 'Third Preference'}
                    <span className="text-blue-600 font-normal ml-2">(required)</span>
                  </h3>
                  {preferredDates[index].date && preferredDates[index].time_slot && (
                    <CheckCircle className="w-5 h-5 text-emerald-500 ml-auto" />
                  )}
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Date Selection */}
                  <div>
                    <Label className="flex items-center gap-2 mb-3">
                      <CalendarDays className="w-4 h-4 text-slate-400" />
                      Select Date
                    </Label>
                    <Calendar
                      mode="single"
                      selected={preferredDates[index].date}
                      onSelect={(date) => updatePreferredDate(index, 'date', date)}
                      disabled={isDateDisabled}
                      className="rounded-lg border"
                      data-testid={`calendar-${index}`}
                    />
                    <p className="text-xs text-slate-500 mt-2">
                      Available Monday-Saturday, 5-30 days from today
                    </p>
                  </div>

                  {/* Time Slot Selection */}
                  <div>
                    <Label className="flex items-center gap-2 mb-3">
                      <Clock className="w-4 h-4 text-slate-400" />
                      Select Time Window
                    </Label>
                    <div className="space-y-3">
                      {TIME_SLOTS.map((slot) => {
                        const isTaken = currentDate && isSlotTaken(index, currentDate, slot.id);
                        const isSelected = preferredDates[index].time_slot === slot.id;
                        
                        return (
                          <button
                            key={slot.id}
                            type="button"
                            onClick={() => !isTaken && updatePreferredDate(index, 'time_slot', slot.id)}
                            disabled={isTaken}
                            className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                              isSelected
                                ? 'border-blue-500 bg-blue-50'
                                : isTaken
                                ? 'border-slate-200 bg-slate-100 opacity-50 cursor-not-allowed'
                                : 'border-slate-200 hover:border-slate-300'
                            }`}
                            data-testid={`time-slot-${index}-${slot.id}`}
                          >
                            <div className="flex items-center justify-between">
                              <span className={`font-medium ${isTaken ? 'text-slate-400' : ''}`}>
                                {slot.label}
                              </span>
                              {isSelected && (
                                <Check className="w-5 h-5 text-blue-600" />
                              )}
                              {isTaken && !isSelected && (
                                <span className="text-xs text-slate-400">Already selected</span>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>

                    {preferredDates[index].date && (
                      <div className="mt-4 p-3 bg-slate-100 rounded-lg">
                        <p className="text-sm text-slate-600">
                          <span className="font-medium">Selected:</span>{' '}
                          {format(preferredDates[index].date, 'EEEE, MMMM d, yyyy')}
                          {preferredDates[index].time_slot && (
                            <span className="block mt-1">
                              {TIME_SLOTS.find(s => s.id === preferredDates[index].time_slot)?.label}
                            </span>
                          )}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
